import { axiosInstance } from "../axios.config";

export interface InviteResult {
  email: string;
  status: "sent" | "pending_connection" | "error";
  message?: string;
}

export interface InviteBuyersResponse {
  successCount: number;
  failureCount: number;
  results: InviteResult[];
}

export interface InvitationInfo {
  inviterName: string;
  inviterPhotoUrl: string | null;
  inviterEmail: string;
  invitedEmail: string;
}

export interface InviteBuyersPayload {
  emails: string[];
  personalMessage?: string;
  /** Inviter self-represents an active real estate licence (Addendum B.5) */
  licenseAttested?: boolean;
  /** FR-13: the invitee's company */
  companyName?: string;
  // PRD 2.1 AC2 / 7.1: seller invites by licensed agents only (never on buyer invites, AC5)
  sellerInvite?: boolean;
  referralFeeType?: "percent" | "flat";
  referralFeeValue?: number;
  /** FR-03b / PRD 2.1 AC3: licensed agent's buyer invite only; decides the buyer's pop-up on an accepted offer */
  agreementType?: AgreementType;
  /** Informational fee agreed outside the platform (fee_outside only); never charged, never in escrow */
  agreementFeeAmount?: number;
}

export type AgreementType = "relist" | "fee_outside" | "none";

export type ConnectionStatus = "PENDING" | "ACCEPTED" | "DECLINED" | "BLOCKED";

export interface SentInvitation {
  id: string;
  email: string;
  status: ConnectionStatus;
  invitedViaEmail: boolean;
  createdAt: string;
  targetUser?: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl: string | null;
  };
}

export interface SourceUser {
  id: string;
  firstName: string;
  lastName: string;
  photoUrl: string | null;
  role: string;
}

export interface ConnectionWithUser {
  id: string;
  status: ConnectionStatus;
  createdAt: string;
  sourceUser: SourceUser;
}

export interface UserConnection {
  id: string;
  sourceUserId: string;
  targetUserId: string | null;
  status: ConnectionStatus;
  createdAt: string;
  acceptedAt: string | null;
}

export interface LicenseInfo {
  licenseNumber: string;
  licenseState: string;
  licenseType: "Agent" | "Broker";
}

export interface TargetUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  photoUrl: string | null;
  role: string; // buyer, seller, agent, operator
  tier: number; // 1=Basic, 2=Verified, 3=Pro, 4=Certified Pro
  licenseInfo?: LicenseInfo | null;
}

export interface ConnectionWithTargetUser {
  id: string;
  status: ConnectionStatus;
  invitedViaEmail: boolean;
  isInviter: boolean; // true = current user invited them, false = they invited current user
  createdAt: string;
  acceptedAt: string | null;
  targetUser: TargetUser;
}

export const connectionsService = {
  /**
   * Invite users to join FlipIQ
   * Creates connections for existing users or sends email invitations for new users
   *
   * @param payload - Emails and optional personal message
   */
  inviteBuyers: async (
    payload: InviteBuyersPayload
  ): Promise<InviteBuyersResponse> => {
    const response = await axiosInstance.post<InviteBuyersResponse>(
      "/connections/invite",
      payload
    );
    return response.data;
  },

  /**
   * Get invitation information by token
   * Public endpoint - used on registration page to show inviter info
   *
   * @param token - Invitation token from URL
   */
  getInvitationInfo: async (token: string): Promise<InvitationInfo> => {
    const response = await axiosInstance.get<InvitationInfo>(
      `/connections/invitation-info?token=${encodeURIComponent(token)}`
    );
    return response.data;
  },

  /**
   * Get pending connection requests for the current user
   * Returns connections where the user is the target and status is PENDING
   */
  getPendingConnections: async (): Promise<ConnectionWithUser[]> => {
    const response = await axiosInstance.get<ConnectionWithUser[]>(
      "/connections/pending"
    );
    return response.data;
  },

  /**
   * Accept a pending connection request
   * Only the target user can accept their own connection requests
   *
   * @param connectionId - ID of the connection to accept
   */
  acceptConnection: async (connectionId: string): Promise<UserConnection> => {
    const response = await axiosInstance.put<UserConnection>(
      `/connections/${connectionId}/accept`
    );
    return response.data;
  },

  /**
   * Decline a pending connection request
   * Only the target user can decline their own connection requests
   *
   * @param connectionId - ID of the connection to decline
   */
  declineConnection: async (connectionId: string): Promise<void> => {
    await axiosInstance.put(`/connections/${connectionId}/decline`);
  },

  /**
   * Get accepted connections for the current user
   * Returns connections where the user is the source (network connections)
   *
   * @param status - Optional filter by status (defaults to ACCEPTED)
   */
  getConnections: async (
    status?: ConnectionStatus
  ): Promise<ConnectionWithTargetUser[]> => {
    const params = status ? { status } : {};
    const response = await axiosInstance.get<ConnectionWithTargetUser[]>(
      "/connections",
      { params }
    );
    return response.data;
  },

  /**
   * Remove a connection
   * Soft deletes by changing status to BLOCKED
   *
   * @param connectionId - ID of the connection to remove
   */
  removeConnection: async (connectionId: string): Promise<void> => {
    await axiosInstance.delete(`/connections/${connectionId}`);
  },

  /**
   * Get count of accepted connections for the current user
   * Story 6.7: Used to determine if network notifications can be sent
   */
  getConnectionCount: async (): Promise<number> => {
    const response = await axiosInstance.get<{ count: number }>(
      "/connections/count"
    );
    return response.data.count;
  },

  /**
   * Get pending invitations sent by the current user
   * Story 6.2: Used for Invite Buyers page pending invitations list
   */
  getSentInvitations: async (): Promise<SentInvitation[]> => {
    const response = await axiosInstance.get<SentInvitation[]>(
      "/connections/sent-invitations"
    );
    return response.data;
  },

  /**
   * Resend an invitation email
   * Story 6.2: Used for Invite Buyers page resend functionality
   */
  resendInvitation: async (
    connectionId: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
    }>(`/connections/${connectionId}/resend`);
    return response.data;
  },
};
